import './styles.scss';

import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import Lightbox from 'react-image-lightbox';
import { toast as Toast } from 'react-toastify';
import { Card, CardHeader, Modal, Table } from 'reactstrap';

import {
  findSimilarCigars,
  type FindSimilarResponse,
  generateCigarEmbeddings,
  getEmbeddingSuccessMessage,
  type HydratedSimilarityCandidate,
  hydrateSimilarityCandidates,
} from '@/api/cigar-matching';
import Breadcrumb from '@/components/common/breadcrumb.component';
import DebounceSearchBar from '@/components/debounce-search-bar';
import Icon from '@/components/icon';
import ModalDialog from '@/components/modal-dialog';
import Button from '@/components/overrides/button';
import StandalonePagination from '@/components/pagination/standalone';
import RowMoreOptions from '@/components/table/row-more-options';
import ToastContent from '@/components/toasts/content';
import { Constants } from '@/constants';

let nextMarker = null;

const PAGE_SIZE = 30;

const markers = localStorage.getItem('cigar-reference-image-s3-markers')
  ? JSON.parse(localStorage.getItem('cigar-reference-image-s3-markers'))
  : {};

const getApiErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

function CigarBandRecognition() {
  const [cigars, setCigars] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('training');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedCigarId, setSelectedCigarId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [imageCount, setImageCount] = useState(0);

  const [urlToImport, setUrlToImport] = useState('');
  const [isImportingImage, setIsImportingImage] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);

  const [embeddingCigarIds, setEmbeddingCigarIds] = useState<Set<string>>(
    new Set()
  );
  const [similarityImageKeys, setSimilarityImageKeys] = useState<Set<string>>(
    new Set()
  );
  const [similarityResult, setSimilarityResult] =
    useState<FindSimilarResponse | null>(null);
  const [similarityCandidates, setSimilarityCandidates] = useState<
    HydratedSimilarityCandidate[]
  >([]);
  const [showSimilarityModal, setShowSimilarityModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const embeddingRequests = useRef(new Set<string>());
  const similarityRequests = useRef(new Set<string>());

  const getRecords = async () => {
    setIsLoadingRecords(true);
    try {
      const res = await axios.get(
        `${Constants.apiPath}/actions/cigars/images/objects`,
        {
          params: {
            next_marker: markers[currentPage],
          },
        }
      );
      if (res.data?.folders) {
        const records = res.data.folders.map((folder, index) => ({
          id: folder,
          references: {
            training: res.data.training_data[index] || [],
            tests: res.data.test_data[index] || [],
          },
        }));
        const hydratedRecords = await Promise.all(
          records.map(async (record) => {
            try {
              const cigarResponse = await axios.get(
                `${Constants.clientPath}/cigars/${record.id}`
              );
              return {
                ...record,
                fullName: cigarResponse.data?.full_name || '',
              };
            } catch {
              return { ...record, fullName: '' };
            }
          })
        );
        setCigars(hydratedRecords);

        if (res.data.marker) {
          nextMarker = res.data.marker;
          markers[currentPage + 1] = nextMarker;

          localStorage.setItem(
            'cigar-reference-image-s3-markers',
            JSON.stringify(markers)
          );
        }
      }
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to load recognition images"
          message="The recognition image list could not be loaded."
        />,
        { position: 'top-right' }
      );
    } finally {
      setIsLoadingRecords(false);
    }
  };

  const filteredCigars = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return cigars;
    return cigars.filter(
      (cigar) =>
        String(cigar.id).toLowerCase().includes(query) ||
        cigar.fullName?.toLowerCase().includes(query)
    );
  }, [cigars, searchText]);

  const deleteImage = (url) => {
    const parts = url.split('/');

    axios
      .delete(`${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`, {
        params: {
          key: parts[parts.length - 1].split('?')[0],
          type: selectedType,
        },
      })
      .then(() => {
        const updatedImages = selectedImages.filter((item) => item !== url);

        setSelectedImages(updatedImages);

        setCigars(
          cigars.map((cigar) => {
            if (cigar.id === selectedCigarId) {
              cigar.references[selectedType] = updatedImages;
            }

            return cigar;
          })
        );

        ModalDialog.close();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const importImageFromUrl = async () => {
    const imageUrl = urlToImport.trim();
    if (!imageUrl || isImportingImage) return;

    try {
      setIsImportingImage(true);
      await axios.put(
        `${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`,
        {
          image_url: imageUrl,
          type: selectedType,
        }
      );
      const updatedImages = selectedImages.concat(imageUrl);
      setSelectedImages(updatedImages);
      setCigars(
        cigars.map((cigar) =>
          cigar.id === selectedCigarId
            ? {
                ...cigar,
                references: {
                  ...cigar.references,
                  [selectedType]: updatedImages,
                },
              }
            : cigar
        )
      );
      setUrlToImport('');
      Toast.success(
        <ToastContent title="Image added" message="The image was imported." />,
        { position: 'top-right' }
      );
    } catch (err) {
      Toast.error(
        <ToastContent
          title="Unable to import image"
          message={getApiErrorMessage(err, 'The image could not be imported.')}
        />,
        { position: 'top-right' }
      );
    } finally {
      setIsImportingImage(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${Constants.apiPath}/actions/cigars/images/stats`, {
        params: {
          next_marker: markers[currentPage],
        },
      })
      .then((res) => {
        setTotalCount(res.data.label_count);
        setImageCount(res.data.image_count);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    getRecords();
  }, [currentPage]);

  const renderImageGrid = (cigarId, images, type) => {
    return (
      <button
        type="button"
        className="recognition-image-grid"
        onClick={() => {
          setSelectedType(type);
          setSelectedImages(images);
          setShowModal(true);
          setSelectedCigarId(cigarId);
        }}
      >
        {images?.slice(0, 5).map((image) => (
          <span className="recognition-thumbnail" key={image}>
            <img src={image} alt="Cigar" />
          </span>
        ))}

        <span className="recognition-image-count">
          {images?.length || 0} {images?.length === 1 ? 'image' : 'images'}
          <Icon name={images?.length ? 'chevron-right' : 'plus'} size={14} />
        </span>
      </button>
    );
  };

  const searchGoogle = (cigarId) => {
    axios
      .get(`${Constants.clientPath}/cigars/${cigarId}`)
      .then((res) => {
        if (res.data?.full_name) {
          window.open(
            `https://www.google.com/search?tbm=isch&q=${res.data.full_name}`,
            '_blank'
          );
        }
      })
      .catch((e) => {
        console.log(e.message);
        alert('Unable to get cigar details');
      });
  };

  const setEmbeddingPending = (cigarId, pending) => {
    setEmbeddingCigarIds((current) => {
      const next = new Set(current);
      if (pending) next.add(String(cigarId));
      else next.delete(String(cigarId));
      return next;
    });
  };

  const generateEmbeddings = async (cigar, force = false) => {
    const cigarId = String(cigar.id);
    if (embeddingRequests.current.has(cigarId)) return;
    try {
      embeddingRequests.current.add(cigarId);
      setEmbeddingPending(cigar.id, true);
      const result = await generateCigarEmbeddings(cigar.id, {
        maximumImages: 10,
        ...(force ? { force: true } : {}),
      });
      Toast.success(
        <ToastContent
          title="Embeddings"
          message={getEmbeddingSuccessMessage(result)}
        />,
        { position: 'top-right' }
      );
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to generate embeddings"
          message={getApiErrorMessage(
            err,
            'There was a problem generating the reference embeddings.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      embeddingRequests.current.delete(cigarId);
      setEmbeddingPending(cigar.id, false);
    }
  };

  const findSimilar = async (cigar) => {
    const imageUrls = cigar.references.tests || [];
    const imageKey = imageUrls.join('|');
    if (!imageUrls.length || similarityRequests.current.has(imageKey)) return;

    try {
      similarityRequests.current.add(imageKey);
      setSimilarityImageKeys((current) => new Set(current).add(imageKey));
      const result = await findSimilarCigars(imageUrls);
      const hydrated = await hydrateSimilarityCandidates(
        result.candidates || []
      );
      setSimilarityResult(result);
      setSimilarityCandidates(hydrated);
      setShowSimilarityModal(true);
    } catch (err) {
      console.error(err);
      Toast.error(
        <ToastContent
          title="Unable to find similar cigars"
          message={getApiErrorMessage(
            err,
            'There was a problem running the similarity search.'
          )}
        />,
        { position: 'top-right' }
      );
    } finally {
      similarityRequests.current.delete(imageKey);
      setSimilarityImageKeys((current) => {
        const next = new Set(current);
        next.delete(imageKey);
        return next;
      });
    }
  };

  const confirmRegeneration = (cigar) => {
    ModalDialog.show({
      title: 'Regenerate embeddings?',
      message:
        'This is slower and will upsert embeddings for the current model and version.',
      type: 'warning',
      buttons: [
        { label: 'Cancel', onClick: () => ModalDialog.close() },
        {
          label: 'Regenerate',
          color: 'warning',
          onClick: () => {
            ModalDialog.close();
            generateEmbeddings(cigar, true);
          },
        },
      ],
    });
  };

  const renderAiActions = (cigar) => {
    const embeddingPending = embeddingCigarIds.has(String(cigar.id));
    const similarityPending = similarityImageKeys.has(
      (cigar.references.tests || []).join('|')
    );
    const hasTestImages = Boolean(cigar.references.tests?.length);

    return (
      <RowMoreOptions
        items={[
          {
            type: 'MenuItem',
            title: embeddingPending
              ? 'Generating embeddings…'
              : 'Generate embeddings',
            onClick: () => {
              if (!embeddingPending) generateEmbeddings(cigar);
            },
            style: embeddingPending ? { opacity: 0.55 } : undefined,
          },
          {
            type: 'MenuItem',
            title: 'Regenerate embeddings',
            onClick: () => {
              if (!embeddingPending) confirmRegeneration(cigar);
            },
            style: embeddingPending ? { opacity: 0.55 } : undefined,
          },
          { type: 'Divider' },
          {
            type: 'MenuItem',
            title: similarityPending ? 'Searching…' : 'Find similar',
            onClick: () => {
              if (!similarityPending && hasTestImages) findSimilar(cigar);
            },
            style:
              similarityPending || !hasTestImages
                ? { opacity: 0.55 }
                : undefined,
          },
        ]}
      />
    );
  };

  const renderSimilarityModal = () => (
    <Modal
      isOpen={showSimilarityModal}
      toggle={() => setShowSimilarityModal(false)}
      className="modal-dialog-lg"
    >
      <div className="modal-header">
        <h5 className="modal-title">
          {similarityResult?.matched
            ? 'Confirmed similarity match'
            : 'Similarity suggestions'}
        </h5>
      </div>
      <div className="modal-body">
        {!similarityCandidates.length ? (
          <p>No similar embedded reference images were found.</p>
        ) : (
          <Table responsive>
            <thead>
              <tr>
                <th>Cigar</th>
                <th>Match confidence</th>
                <th>Similarity</th>
                <th>Supporting references</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {similarityCandidates.map((candidate, index) => {
                const confirmed = Boolean(
                  similarityResult?.matched && index === 0
                );
                return (
                  <tr
                    key={`${candidate.cigar_id}-${index}`}
                    className={
                      confirmed ? 'similarity-confirmed-match' : undefined
                    }
                  >
                    <td>
                      {candidate.cigarName || 'Unknown cigar'}
                      <small className="d-block text-muted">
                        ID {candidate.cigar_id}
                      </small>
                    </td>
                    <td title="Similarity-based confidence used for evaluation; not yet statistically calibrated.">
                      {(candidate.confidence * 100).toFixed(1)}%
                    </td>
                    <td>{candidate.similarity.toFixed(3)}</td>
                    <td>{candidate.supporting_references}</td>
                    <td>{confirmed ? 'Automatic match' : 'Suggestion'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {similarityResult?.decisionReason && (
          <small className="text-muted">
            Decision reason: {similarityResult.decisionReason}
          </small>
        )}
      </div>
      <div className="modal-footer">
        <Button onClick={() => setShowSimilarityModal(false)}>Close</Button>
      </div>
    </Modal>
  );

  const renderModal = () => (
    <Modal
      isOpen={showModal}
      onClosed={() => setShowModal(false)}
      toggle={() => setShowModal(false)}
      className="modal-dialog-lg recognition-images-modal"
    >
      <div className="modal-header recognition-modal-header">
        <div>
          <h5 className="modal-title" id="cigar-modal-title">
            {selectedType === 'tests' ? 'Test Images' : 'Training Images'}
          </h5>
          <p>
            Cigar {selectedCigarId} · {selectedImages.length}{' '}
            {selectedImages.length === 1 ? 'image' : 'images'}
          </p>
        </div>
        <button
          type="button"
          className="recognition-modal-close"
          aria-label="Close"
          onClick={() => setShowModal(false)}
        >
          ×
        </button>
      </div>

      <div className="modal-body recognition-modal-body">
        <section className="recognition-modal-section">
          <div className="recognition-modal-section-heading">
            <div>
              <h6>Image gallery</h6>
              <span>Hover an image to preview or remove it.</span>
            </div>
          </div>

          {selectedImages.length ? (
            <div className="recognition-modal-gallery">
              {selectedImages.slice(0, 20).map((image, index) => (
                <div className="recognition-modal-image" key={image}>
                  <img src={image} alt={`Cigar image ${index + 1}`} />
                  <div className="recognition-modal-image-actions">
                    <button
                      type="button"
                      title="View full size"
                      onClick={() => {
                        setSelectedIndex(index);
                        setShowLightbox(true);
                      }}
                    >
                      <Icon name="maximize2" size={17} />
                    </button>
                    <button
                      type="button"
                      className="delete"
                      title="Delete image"
                      onClick={() => {
                        ModalDialog.show({
                          title: 'Delete image?',
                          message:
                            'This image will be removed from the cigar image library.',
                          type: 'error',
                          buttons: [
                            {
                              label: 'Cancel',
                              onClick: () => ModalDialog.close(),
                            },
                            {
                              label: 'Delete',
                              style: { color: 'rgb(239, 81, 100)' },
                              onClick: () => deleteImage(image),
                            },
                          ],
                        });
                      }}
                    >
                      <Icon name="trash" size={17} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="recognition-modal-empty">
              <Icon name="image" size={28} />
              <strong>No images yet</strong>
              <span>Add the first image below.</span>
            </div>
          )}
        </section>

        <section className="recognition-modal-section recognition-add-images">
          <div className="recognition-modal-section-heading">
            <div>
              <h6>Add an image</h6>
              <span>Upload an image or import one from a public URL.</span>
            </div>
          </div>

          <Dropzone
            id="add-images-dropzone"
            config={{
              postUrl: 'no-url',
              iconFiletypes: ['.jpg', '.png'],
            }}
            djsConfig={{
              dictDefaultMessage: 'Drop images here',
            }}
          >
            {({ getRootProps, getInputProps }) => (
              <div className="recognition-dropzone" {...getRootProps()}>
                <input {...getInputProps()} />
                <Icon name="upload-cloud" size={24} />
                <strong>Drop an image here or click to browse</strong>
                <span>JPG or PNG</span>
              </div>
            )}
          </Dropzone>

          <div className="recognition-import-divider">
            <span>or</span>
          </div>

          <div className="recognition-url-import">
            <label htmlFor="recognition-image-url">Image URL</label>
            <div className="input-group">
              <input
                id="recognition-image-url"
                className="form-control"
                type="url"
                name="image-url"
                placeholder="https://example.com/cigar.jpg"
                value={urlToImport}
                onChange={(e) => setUrlToImport(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') importImageFromUrl();
                }}
              />

              <div className="input-group-append">
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!urlToImport.trim() || isImportingImage}
                  onClick={importImageFromUrl}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                >
                  {isImportingImage ? 'Importing…' : 'Import image'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="modal-footer recognition-modal-footer">
        <Button color="light" onClick={() => setShowModal(false)}>
          Done
        </Button>
      </div>
    </Modal>
  );

  return (
    <div>
      <Breadcrumb
        title="Cigar Band Recognition"
        label="Band Recognition"
        parent="Cigar Manager"
      />

      <div className="container-fluid" style={{ marginTop: 20 }}>
        <Card className="recognition-manager-card">
          <CardHeader className="recognition-manager-header">
            <div>
              <h5>Cigar image library</h5>
              <p>
                Review training and test images, generate embeddings, and run
                similarity checks.
              </p>
            </div>
            <div className="recognition-stats">
              <strong>{imageCount.toLocaleString()}</strong>
              <span>reference images</span>
            </div>
          </CardHeader>

          <div className="recognition-toolbar">
            <div className="recognition-search">
              <Icon name="search" size={16} />
              <DebounceSearchBar
                value={searchText}
                placeholder="Filter this page by cigar ID or name..."
                onSearch={setSearchText}
              />
              {searchText && (
                <button
                  type="button"
                  className="recognition-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearchText('')}
                >
                  ×
                </button>
              )}
            </div>
            <span className="recognition-result-count">
              {filteredCigars.length} of {cigars.length} cigars on this page
            </span>
          </div>

          <div className="table-responsive">
            <Table className="recognition-table" hover>
              <thead>
                <tr>
                  <th scope="col">Cigar</th>
                  <th scope="col">Reference Images</th>
                  <th scope="col">Test Images</th>
                  <th scope="col" aria-label="AI actions" />
                </tr>
              </thead>

              <tbody>
                {filteredCigars.map((cigar) => (
                  <tr key={cigar.id}>
                    <td className="recognition-cigar-cell">
                      <div className="recognition-cigar-name">
                        {cigar.fullName || 'Name unavailable'}
                      </div>
                      <div className="recognition-cigar-meta">
                        <a
                          href={`/cigars/manager?id=${cigar.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ID {cigar.id}
                        </a>
                        <button
                          type="button"
                          title="Search Google Images"
                          onClick={() => searchGoogle(cigar.id)}
                        >
                          <Icon name="search" size={13} />
                          Google Images
                        </button>
                      </div>
                    </td>

                    <td>
                      {renderImageGrid(
                        cigar.id,
                        cigar.references.training,
                        'training'
                      )}
                    </td>

                    <td>
                      {renderImageGrid(
                        cigar.id,
                        cigar.references.tests,
                        'tests'
                      )}
                    </td>

                    <td className="recognition-actions-cell">
                      {renderAiActions(cigar)}
                    </td>
                  </tr>
                ))}
                {!isLoadingRecords && filteredCigars.length === 0 && (
                  <tr>
                    <td colSpan={4} className="recognition-empty-state">
                      <Icon name="search" size={24} />
                      <strong>No cigars match this filter</strong>
                      <span>Try a different cigar ID or name.</span>
                    </td>
                  </tr>
                )}
                {isLoadingRecords && (
                  <tr>
                    <td colSpan={4} className="recognition-empty-state">
                      <strong>Loading cigar images…</strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="recognition-pagination">
            <span className="recognition-pagination-total">
              Showing {totalCount ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to{' '}
              {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <StandalonePagination
              manual
              paginationProps={{
                page: currentPage,
                sizePerPage: PAGE_SIZE,
                totalSize: totalCount,
                onPageChange: setCurrentPage,
              }}
            />
          </div>
        </Card>
      </div>

      {renderModal()}
      {renderSimilarityModal()}

      {showLightbox && (
        <Lightbox
          mainSrc={selectedImages[selectedIndex]}
          nextSrc={selectedImages[(selectedIndex + 1) % selectedImages.length]}
          prevSrc={
            selectedImages[
              (selectedIndex + selectedImages.length - 1) %
                selectedImages.length
            ]
          }
          imageTitle={`${selectedIndex + 1}/${selectedImages.length}`}
          onCloseRequest={() => setShowLightbox(false)}
          onMovePrevRequest={() =>
            setSelectedIndex(
              (selectedIndex + selectedImages.length - 1) %
                selectedImages.length
            )
          }
          onMoveNextRequest={() =>
            setSelectedIndex((selectedIndex + 1) % selectedImages.length)
          }
        />
      )}
    </div>
  );
}

export default CigarBandRecognition;
